import { useEffect, useState } from "react";

import Sentry from "@similarweb/sentry-logger";

import { allTrackers } from "app/services/track/track";

interface IEmailTrackerProps {
    paramName?: string;
}

interface IEmailLinkClickEventData {
    event: string;
    properties: {
        feature: string;
        triggerId: string;
        notificationId: string;
        category: string;
    };
}

function isEmailLinkClickEventData(data: unknown): data is IEmailLinkClickEventData {
    return (
        typeof data === "object" &&
        data !== null &&
        "event" in data &&
        typeof (data as IEmailLinkClickEventData).event === "string" &&
        "properties" in data &&
        typeof (data as IEmailLinkClickEventData).properties === "object"
    );
}

export const EmailTracker = ({ paramName = "clickdata" }: IEmailTrackerProps) => {
    const [isTracked, setIsTracked] = useState(false);

    useEffect(() => {
        if (isTracked) {
            return;
        }

        try {
            const params = new URLSearchParams(window.location.search);
            const trackingDataEncodedBase64 = params.get(paramName);

            if (!trackingDataEncodedBase64) {
                return;
            }

            let trackingData: unknown;
            try {
                trackingData = JSON.parse(atob(trackingDataEncodedBase64));
            } catch (error) {
                Sentry.error("EmailTracker: Failed to decode or parse tracking data:", error);
                return;
            }

            if (!isEmailLinkClickEventData(trackingData)) {
                return;
            }

            allTrackers.dispatchTrackEvent(trackingData.event, "click", trackingData.properties.category, {
                ...trackingData.properties,
            });

            setIsTracked(true);

            params.delete(paramName);
            const newUrl =
                window.location.pathname + (params.toString() ? `?${params.toString()}` : "") + window.location.hash;
            window.history.replaceState({}, "", newUrl);
        } catch (error) {
            Sentry.error("Error in EmailTracker:", error);
        }
    }, [paramName, isTracked]);

    return null;
};
